import { HtmlComponent } from './../html-component';
import { Gui } from './../../gui';
import { ColorConfig } from './../../color-config';
import { Geometry } from './../../../util/geometry';
import { Colors } from './../../../util/colors';
import * as Snap from 'snapsvg-cjs';

// !! the legend is not cleanly programmed.
// the css classes from the boxes are completely independently positioned from the svg that
// draws on top. So be careful when moving things.

// import html
import template from './legend.hogan.html';

export class Legend extends HtmlComponent {
    private active: boolean = false;
    public snap: any;
    private legend: JQuery;
    private legendOverlay: JQuery;
    // config
    private pixelSize = 30;
    private startVertical = 200;
    private marginVertical = 40;
    private marginHorizontal = 40;
    private sizeOfConsultantsMenu = 245;

    // calculated
    private height: number;
    private width: number;
    private organigramSize: number;
    private middleOfScreen: number;
    private smallest: number;
    private visible: boolean = false;

    // state
    private currentVertical: number = this.startVertical;

    // svg elements
    private techConsultant: any;
    private techConsultantText: any;
    private nonTechConsultant: any;
    private nonTechConsultantText: any;
    // private colorAndText1: any;
    // private colorAndText2: any;
    // private colorAndText3: any;
    // private colorAndText4: any;
    private filteredOutText: any;
    private filteredOutFutureText: any;
    private colorAndText8: any;
    private xpCirclesAndText: any;

    // note, Legend is a hack with many absolutely positioned items.
    // Ideally, you should make a component of each of the windows
    // so they insert their own html (instead of hooking on to placeholders)
    // and so that they take care of their own rescaling
    constructor(private gui: Gui) {
        super(template, {});
        this.snap = new Snap(this.jqueryElement.find('#legend-svg')[0]);
        this.legend = this.jqueryElement.find('#legend');
        this.legendOverlay = this.jqueryElement.find('#legend-fadeout-overlay');

        this.jqueryElement.find('#info-button').click(this.handleClick);
        this.legend.click(this.handleClick);
        this.initLegend();
        this.legend.hide();
        this.initResizeMonitoring();
    }

    private handleClick = () => {
        if (this.active) {
            this.visible = false;
            this.legendOverlay.addClass('info-deactive');
            this.legendOverlay.removeClass('info-active');
            this.legend.hide();
        } else {
            this.visible = true;
            this.legendOverlay.addClass('info-active');
            this.rescale();
            this.legend.show();
            this.legendOverlay.removeClass('info-deactive');
        }
        this.active = !this.active;
    }

    private initResizeMonitoring() {
        window.addEventListener('resize', e => {
            if (this.visible) {
                this.rescale();
            }
        });
    }

    // rescale is naive atm. Just clear and redraw.
    private rescale = () => {
        this.currentVertical = this.startVertical;
        this.height = this.legend.height();
        this.width = this.legend.width();
        this.organigramSize = this.gui.organigramComponent.jqueryElement.width();
        this.middleOfScreen = this.width - this.sizeOfConsultantsMenu - this.organigramSize; // organigram is bigger so it is not half
        this.smallest = Math.min(this.height, this.width);

        // tech consultant
        let matrix = new Snap.Matrix();

        let x3 = this.middleOfScreen + this.marginHorizontal - this.pixelSize * 1.5 + 30;
        let y3 = this.height - this.currentVertical - this.pixelSize / 2;
        matrix.translate(x3, y3);
        matrix.scale(this.pixelSize / this.smallest, this.pixelSize / this.smallest);

        this.techConsultant.transform(matrix);
        this.techConsultantText.attr({
            x: x3 + this.pixelSize * 2,
            y: y3 + this.pixelSize / 2
        });
        this.currentVertical = this.currentVertical + this.marginVertical;

        // non tech consultant
        matrix = new Snap.Matrix();
        let x4 = this.middleOfScreen + this.marginHorizontal - this.pixelSize * 1.5 + 30;
        let y4 = this.height - this.currentVertical - this.pixelSize / 2;
        matrix.translate(x4, y4);
        matrix.scale(this.pixelSize / this.smallest, this.pixelSize / this.smallest);

        this.nonTechConsultant.transform(matrix);
        this.nonTechConsultantText.attr({
            x: x4 + this.pixelSize * 2,
            y: y4 + this.pixelSize / 2
        });

        this.rescaleColorLegend(this.filteredOutText);
        this.rescaleColorLegend(this.colorAndText8);

        this.rescaleExperience(this.xpCirclesAndText);
    }

    private initLegend() {
        // calculated
        this.currentVertical = this.startVertical;
        this.height = this.legend.height();
        this.width = this.legend.width();
        this.organigramSize = this.gui.organigramComponent.jqueryElement.width();
        this.middleOfScreen = this.width - this.sizeOfConsultantsMenu - this.organigramSize; // organigram is bigger so it is not half
        this.smallest = Math.min(this.height, this.width);

        if (!this.smallest) {
            setTimeout(() => this.initLegend(), 100);
            return;
        }

        let techSymbol = Snap.load('img/tech.svg', (svgFragmentTech: any) => {
            let nontechSymbol = Snap.load('img/nontech.svg', (svgFragmentNontech: any) => {
                // TECH
                let svgSymbol = this.snap.group();
                svgSymbol.append(svgFragmentTech);

                svgSymbol.selectAll('path').attr({
                    fill: ColorConfig.hidden,
                    stroke: ColorConfig.infoColorText,
                    'stroke-width': '2'
                });
                this.techConsultant = svgSymbol;
                this.techConsultantText = this.snap.text(0, 0, 'Technical').attr({
                    fill: ColorConfig.infoColorText
                });

                // NON TECH
                svgSymbol = this.snap.group();
                svgSymbol.append(svgFragmentNontech);

                svgSymbol.selectAll('path').attr({
                    fill: ColorConfig.hidden,
                    stroke: ColorConfig.infoColorText,
                    'stroke-width': '1'
                });
                let t4 = this.snap.text(0, 0, 'Non technical').attr({
                    fill: ColorConfig.infoColorText
                });
                this.nonTechConsultant = svgSymbol;
                this.nonTechConsultantText = t4;

                this.filteredOutText = this.generateColorLegend(
                    'Filtered out',
                    ColorConfig.organigramConsultantEnabled
                );
                this.colorAndText8 = this.generateColorLegend('Selected', ColorConfig.organigramConsultantSelected);


                this.xpCirclesAndText = this.generateExperience(0.5, 'Seniority (more fill = higher seniority)');
            });
        });
    }

    private rescaleColorLegend(colorAndText: any, sideways: boolean = false) {
        let x = this.middleOfScreen + this.marginHorizontal - this.pixelSize + 30;
        if (sideways) {
            x += 140;
        } else {
            this.currentVertical = this.currentVertical + this.marginVertical;
        }
        let y = this.height - this.currentVertical - this.pixelSize / 2;
        colorAndText.path.transform('t' + x + ' ' + y);
        colorAndText.text.attr({ x: x + this.pixelSize * 1.5, y: y + this.pixelSize / 2 });
    }

    private generateColorLegend(text: string, color: string) {
        let path1 = this.snap.path('M 0 8 L 25 0 L 5 18 L 27 5 L 3 25 L 26 15 L 10 29 L 22 25').attr({
            fill: 'none',
            stroke: color,
            strokeWidth: 6,
            x: 200
        });

        let t = this.snap.text(0, 0, text).attr({
            fill: ColorConfig.infoColorText
        });
        return { path: path1, text: t };
    }

    private generateExperience(consultantValueBias: number, text: string) {
        let circle1 = this.snap.circle(0, 0, 1).attr({
            fill: '#EEEEEE',
            'pointer-events': 'none'
        });

        let circle2 = this.snap.circle(0, 0, 1).attr({
            fill: 'rgba(255,255,255,0.3)',
            'pointer-events': 'none'
        });

        let t = this.snap.text(0, 0, text).attr({ fill: ColorConfig.infoColorText });
        return {
            circle1,
            circle2,
            innercircle: null as any,
            text: t,
            xpMeasure: consultantValueBias
        };
    }

    private rescaleExperience(circlesAndText: any) {
        this.currentVertical = this.currentVertical + this.marginVertical;
        let x = this.middleOfScreen + this.marginHorizontal - this.pixelSize / 2 + 30;
        let y = this.height - this.currentVertical;

        circlesAndText.text.attr({
            x: x + this.pixelSize,
            y: y + this.pixelSize / 8
        });
        circlesAndText.circle1.attr({
            cx: x,
            cy: y,
            r: this.pixelSize / 2
        });
        circlesAndText.circle2.attr({
            cx: x,
            cy: y,
            r: this.pixelSize / 4
        });

        if (circlesAndText.innercircle) {
            circlesAndText.innercircle.remove();
        }
        if (circlesAndText.xpMeasure) {
            // innercircle is hard to 'rescale' so complete redraw
            let cx = x;
            let cy = y;
            let r: number = this.pixelSize / 4;
            let x1: number = cx - r;
            let y1: number = cy + r - r * 2 * circlesAndText.xpMeasure;
            let x2: number = cx + r;
            let y2: number = y1;
            let flipCircle: number = y1 > cy ? 0 : 1;
            let intersections = Geometry.getIntersections([x1, y1], [x2, y2], [cx, cy, r]);
            let i1: number[] = intersections.points.intersection1.coords;
            let i2: number[] = intersections.points.intersection2.coords;

            let innerCirclePart = this.snap
                .path(
                    'M' +
                        i1[0] +
                        ',' +
                        i1[1] +
                        ' a' +
                        r +
                        ',' +
                        r +
                        ' 0 ' +
                        flipCircle +
                        ',0 ' +
                        (i2[0] - i1[0]) +
                        ',' +
                        (i2[1] - i1[1])
                )
                .attr({
                    fill: 'rgba(0,0,0,1)',
                    'pointer-events': 'none'
                });
            circlesAndText.innercircle = innerCirclePart;
        }
    }
}
